/**
 * Реализация раздела Группы трафика для числовых идентификаторов
 */

import React, { ReactNode, useMemo } from "react";
import { Col, DatePicker, Form, Input, Row, Select } from "antd";
import NumberFormat from "react-number-format";
import { Moment } from "moment";
import { combineClasses } from "../../../../../lib/utils/combineClasses";
import { ReactComponent as DeleteIcon } from "../../../../../img/delete-round.svg";
import { ReactComponent as CopyIcon } from "../../../../../img/copy-icon.svg";
import { displayFormat, getSelectOptions, infinityDateHttpFormat } from "../../../../../lib/utils";
import { DictionaryDto } from "../../../../../lib/interfaces";
import { RuleConditionEdit } from "./RuleConditionEdit/RuleConditionEdit";
import { IRuleCondition } from "../../../../../lib/types/IRuleCondition";
import { IOfferListSelectMode } from "../../../../../containers/OfferContainer/context/types";
import "./AggregationRuleEditItem.less";

type FieldName =
  | "aggName"
  | "periodTypeId"
  | "rangeFrom"
  | "rangeTo"
  | "ruleCondition"
  | "fromDate"
  | "toDate";

const fieldNames: Record<Exclude<FieldName, "ruleCondition">, string> = {
  aggName: "Название группы",
  periodTypeId: "Период накопления",
  rangeFrom: "От (шт)",
  rangeTo: "До (шт)",
  fromDate: "Период c",
  toDate: "Период по",
};

interface ComponentProps {
  className?: string;
  requiredFields?: FieldName[];
  availableFields: FieldName[];
  periodTypes: DictionaryDto[];
  newRuleCondition: IRuleCondition;
  idx: number;
  rangeFromValidator: (rangeFrom: number, idx: number) => Promise<void>;
  rangeFromOnBlur: (rangeFrom: number, idx: number) => void;
  rangeToValidator: (rangeTo: number, idx: number) => Promise<void>;
  rangeToOnBlur: (rangeTo: number, idx: number) => void;
  getIncludeListTitle: (aggRuleIdx: number, idx: number) => string;
  getDocumentTypeListTitle: (aggRuleIdx: number, idx: number) => string;
  onRemove: (index: number) => void;
  hideAdd?: true;
  disableFromDate: (date: Moment) => boolean;
  disableToDate: (date: Moment) => boolean;
  hidePeriod?: true; // Скрытие столбцов с датами
  copyAggregationRule: (idx: number) => void;
  offerListSelectMode: IOfferListSelectMode | undefined;
}

export const AggregationRuleEditItem = (props: ComponentProps) => {
  const {
    className,
    availableFields,
    requiredFields = [],
    periodTypes,
    newRuleCondition,
    idx,
    rangeFromValidator,
    rangeFromOnBlur,
    rangeToValidator,
    rangeToOnBlur,
    getIncludeListTitle,
    getDocumentTypeListTitle,
    onRemove,
    hideAdd,
    disableFromDate,
    disableToDate,
    hidePeriod,
    copyAggregationRule,
    offerListSelectMode,
  } = props;

  type ColumnSpans = Record<FieldName | "btns", number>;

  const columnSpans = useMemo<ColumnSpans>(() => {
    const spans: ColumnSpans = {
      aggName: 4,
      periodTypeId: 3,
      rangeFrom: 2,
      rangeTo: 2,
      ruleCondition: 11,
      btns: 2,
      fromDate: 0,
      toDate: 0,
    };
    if (hideAdd) {
      spans.aggName = 5;
      spans.btns = 0;
    }
    if (!hidePeriod) {
      spans.aggName = 4;
      spans.periodTypeId = 3;
      spans.ruleCondition = 9;
      spans.fromDate = 2;
      spans.toDate = 2;
    }
    return spans;
  }, [hideAdd, hidePeriod]);

  const getHeader = (field: FieldName): ReactNode => (
    <>
      <label className="aggregationRule-editItem__fieldLabel">{fieldNames[field]}</label>
      {requiredFields.includes(field) && <span style={{ color: "red" }}>&nbsp;&nbsp;*</span>}
    </>
  );

  const getFieldProps = (field: FieldName) => ({
    className: "form-item-required__hidden-error__mb0",
    rules: [{ required: requiredFields.includes(field) }],
  });

  const rowLayout = {
    gutter: 20,
    style: {
      marginTop: "10px",
    },
  };

  const commonDisable = offerListSelectMode === "select";

  return (
    <div className={combineClasses("aggregationRule-editItem", className)}>
      <Row {...rowLayout}>
        <Col span={columnSpans.aggName}>
          {idx === 0 && getHeader("aggName")}
          <Form.Item name={[idx, "aggName"]} {...getFieldProps("aggName")}>
            <Input disabled={!availableFields.includes("aggName") || commonDisable} allowClear />
          </Form.Item>
        </Col>
        <Col span={columnSpans.periodTypeId}>
          {idx === 0 && getHeader("periodTypeId")}
          <Form.Item name={[idx, "periodTypeId"]} {...getFieldProps("periodTypeId")}>
            <Select
              disabled={!availableFields.includes("periodTypeId") || commonDisable}
              options={getSelectOptions({
                list: periodTypes,
              })}
            />
          </Form.Item>
        </Col>
        <Col span={columnSpans.rangeFrom}>
          {idx === 0 && getHeader("rangeFrom")}
          <Form.Item
            name={[idx, "rangeFrom"]}
            normalize={(value) => value.replace(/ /g, "")}
            rules={[
              {
                validator: (_, rangeFrom) => rangeFromValidator(rangeFrom, idx),
              },
            ]}
            className="form-item-required__hidden-error__mb0"
          >
            <NumberFormat
              thousandSeparator={" "}
              className="ant-input-affix-wrapper inputNumber__hideArrows"
              decimalScale={0}
              fixedDecimalScale
              onBlur={(event) => {
                const rangeFrom = event.target.value.replace(/ /g, "");
                const rangeFromNum = Number(rangeFrom);
                if (!isNaN(rangeFromNum)) {
                  rangeFromOnBlur(rangeFromNum, idx);
                }
              }}
              disabled={!availableFields.includes("rangeFrom") || commonDisable}
              allowNegative={false}
            />
          </Form.Item>
        </Col>
        <Col span={columnSpans.rangeTo}>
          {idx === 0 && getHeader("rangeTo")}
          <Form.Item
            name={[idx, "rangeTo"]}
            normalize={(value) => value.replace(/ /g, "")}
            rules={[
              {
                validator: (_, rangeTo) => rangeToValidator(rangeTo, idx),
              },
            ]}
            className="form-item-required__hidden-error__mb0"
          >
            <NumberFormat
              thousandSeparator={" "}
              className="ant-input-affix-wrapper inputNumber__hideArrows"
              decimalScale={0}
              fixedDecimalScale
              onBlur={(event) => {
                const rangeTo = event.target.value.replace(/ /g, "");
                const rangeToNum = Number(rangeTo);
                if (!isNaN(rangeToNum)) {
                  rangeToOnBlur(rangeToNum, idx);
                }
              }}
              disabled={!availableFields.includes("rangeTo") || commonDisable}
              allowNegative={false}
            />
          </Form.Item>
        </Col>
        <Col span={columnSpans.ruleCondition}>
          <RuleConditionEdit
            aggRuleIdx={idx}
            newRuleCondition={newRuleCondition}
            availableFields={
              availableFields.includes("ruleCondition")
                ? ["includeList", "documentTypeList", "direction"]
                : []
            }
            getDocumentTypeListTitle={getDocumentTypeListTitle}
            getIncludeListTitle={getIncludeListTitle}
            offerListSelectMode={offerListSelectMode}
          />
        </Col>
        <Col span={columnSpans.fromDate}>
          {idx === 0 && getHeader("fromDate")}
          <Form.Item name={[idx, "fromDate"]} {...getFieldProps("fromDate")}>
            <DatePicker
              format={displayFormat}
              disabledDate={disableFromDate}
              disabled={!availableFields.includes("fromDate") || commonDisable}
            />
          </Form.Item>
        </Col>
        <Col span={columnSpans.toDate}>
          {idx === 0 && getHeader("toDate")}
          <Form.Item name={[idx, "toDate"]} {...getFieldProps("toDate")}>
            <DatePicker
              format={displayFormat}
              disabledDate={disableToDate}
              disabled={!availableFields.includes("toDate") || commonDisable}
            />
          </Form.Item>
        </Col>
        {!hideAdd && !commonDisable && (
          <Col span={columnSpans.btns}>
            {idx === 0 && <div className="aggregationRule-editItem__btnsSpace" />}
            <div className="aggregationRule-editItem__btns">
              <CopyIcon
                className="aggregationRule-editItem__copyButton"
                onClick={() => {
                  copyAggregationRule(idx);
                }}
              />
              <DeleteIcon
                className="aggregationRule-editItem__deleteButton"
                onClick={() => {
                  onRemove(idx);
                }}
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};
